import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import withRouter from "../../../../components/Common/withRouter";
import TableContainer from "../../../../components/Common/TableContainer";
import useBonusData from "../Bonus/utilities/useBonusData";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormFeedback,
} from "reactstrap";

import {
  assignAllowanceToEmployee,
  loadAllAllowances,
  loadEmployeeAllowances,
  deleteAllowanceFromEmployee,
  updateAssignedAllowance,
} from "../../../../slice/thunks";

const initialAllowanceState = {
  id: "",
  amount: "",
};

 function AllowanceSettings(props) {
  const dispatch = useDispatch();
  const { employee } = useBonusData();
  const navigate = useNavigate();

  const [allowance, setAllowance] = useState(initialAllowanceState);

  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [selectedAllowance, setSelectedAllowance] = useState(null);

  const [errors, setErrors] = useState({});

  // =========================
  // SELECTOR
  // =========================
  const payrollSelector = useMemo(
    () =>
      createSelector(
        (state) => state.Payroll,
        (payroll) => ({
          allowances: payroll.allowances || [],
          employeeAllowances: payroll.employeeAllowances || [],
          isEmployeeAllowancesLoading:
            payroll.isEmployeeAllowancesLoading,
        })
      ),
    []
  );

  const {
    allowances,
    employeeAllowances,
    isEmployeeAllowancesLoading,
  } = useSelector(payrollSelector);

  // =========================
  // FETCH DATA
  // =========================
  useEffect(() => {
    if (employee?.employeeId) {
      dispatch(loadEmployeeAllowances(employee.employeeId));
      dispatch(loadAllAllowances());
    }
  }, [dispatch, employee?.employeeId]);

  // =========================
  // HELPERS
  // =========================
  const resetForm = () => {
    setAllowance(initialAllowanceState);
    setErrors({});
  };

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.id) {
      newErrors.id = "Allowance is required";
    }

    if (!data.amount || Number(data.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // =========================
  // ADD ALLOWANCE
  // =========================
  const handleAddAllowance = () => {
    if (!validateForm(allowance)) return;

    dispatch(
      assignAllowanceToEmployee({
        data: allowance,
        employeeId: employee.employeeId,
      })
    );

    resetForm();
  };

  // =========================
  // DELETE
  // =========================
  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this allowance?"))
      return;

    dispatch(
      deleteAllowanceFromEmployee({
        employeeAllowanceId: id,
        employeeId: employee.employeeId,
      })
    );
  };

  // =========================
  // OPEN EDIT MODAL
  // =========================
  const openEditModal = (data) => {
    setSelectedAllowance({
      employeeAllowanceId: data.id,
      id: data.allowanceId,
      amount: data.amount,
    });

    setEditModal(true);
  };

  // =========================
  // UPDATE ALLOWANCE
  // =========================
  const handleUpdateAllowance = () => {
    if (!validateForm(selectedAllowance)) return;

    dispatch(
      updateAssignedAllowance({
        employeeAllowanceId:
          selectedAllowance.employeeAllowanceId,
        employeeId: employee.employeeId,
        data: {
          id: selectedAllowance.id,
          amount: selectedAllowance.amount,
        },
      })
    );

    setEditModal(false);
    setSelectedAllowance(null);
  };

  // =========================
  // TABLE COLUMNS
  // =========================
  const columns = useMemo(
    () => [
      {
        header: "Allowance Name",
        accessorKey: "allowanceName",
        enableColumnFilter: false,
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span>{Number(row.original.amount).toLocaleString()}</span>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;

          return (
            <div className="d-flex gap-2">
              <Button
                color="none"
                size="sm"
                outline
                onClick={() => handleDelete(item.id)}
              >
                <i className="fa fa-trash" />
              </Button>
{/* 
              <Button
                color="none"
                size="sm"
                outline
                onClick={() => openEditModal(item)}
              >
                <i className="fa fa-edit" />
              </Button> */}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="container-fluid p-4">

         {/* Header */}
          <div className="heading-parent-sec">
            <div className="heading-parent-sec d-flex align-items-center pt-4">
              {/* Bouton retour */}
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link p-0 me-3"
                style={{ textDecoration: "none", color: "#0d6efd" }}
              >
               <h6> {props.t("staff_management")}</h6>
              </button>
              <h6><span style={{ fontWeight: "bold" }}> /&nbsp; </span> {props.t("allowances")}</h6>
            </div>
          </div>
      {/* ================= CARD ================= */}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Allowance Settings</h5>

          <p className="card-text">
            Configure employee allowances and amounts.
          </p>

          <hr />

          {/* Employee Info */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <h5 className="mb-0">
              Name: {employee?.fullName}
            </h5>

            <Badge color="primary">
              {employee?.employeeStatus}
            </Badge>
          </div>

          {/* ADD FORM */}
          <div className="row">
            {/* Select Allowance */}
            <div className="col-md-4">
              <Label>Select Allowance</Label>

              <Input
                type="select"
                value={allowance.id}
                invalid={!!errors.id}
                onChange={(e) =>
                  setAllowance({
                    ...allowance,
                    id: e.target.value,
                  })
                }
              >
                <option value="">Select Allowance</option>

                {allowances.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.allowanceName}
                  </option>
                ))}
              </Input>

              <FormFeedback>{errors.id}</FormFeedback>
            </div>

            {/* Amount */}
            <div className="col-md-4">
              <Label>Amount</Label>

              <Input
                type="number"
                placeholder="Enter amount"
                value={allowance.amount}
                invalid={!!errors.amount}
                onChange={(e) =>
                  setAllowance({
                    ...allowance,
                    amount: e.target.value,
                  })
                }
              />

              <FormFeedback>{errors.amount}</FormFeedback>
            </div>

            {/* Button */}
            <div className="col-md-4 d-flex align-items-end">
              <Button
                color="primary"
                onClick={handleAddAllowance}
              >
                <i className="fa fa-plus me-1" />
                Add Allowance
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-subtitle mb-3">
            Employee Allowances
          </h5>

          <TableContainer
            columns={columns}
            data={employeeAllowances}
            divClass="table-card-ward"
            tableClass="align-middle"
            theadClass="thead-light table-light"
            isLoading={isEmployeeAllowancesLoading}
          />
        </div>
      </div>

      {/* ================= EDIT MODAL ================= */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <ModalHeader toggle={() => setEditModal(false)}>
          Edit Allowance
        </ModalHeader>

        <ModalBody>
          {selectedAllowance && (
            <div className="row g-3">
              {/* Allowance */}
              <div className="col-12">
                <Label>Allowance</Label>

                <Input
                  type="select"
                  value={selectedAllowance.id}
                  onChange={(e) =>
                    setSelectedAllowance({
                      ...selectedAllowance,
                      id: e.target.value,
                    })
                  }
                >
                  {allowances.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.allowanceName}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Amount */}
              <div className="col-12">
                <Label>Amount</Label>

                <Input
                  type="number"
                  value={selectedAllowance.amount}
                  onChange={(e) =>
                    setSelectedAllowance({
                      ...selectedAllowance,
                      amount: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="light"
            onClick={() => setEditModal(false)}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={handleUpdateAllowance}
          >
            <i className="fa fa-save me-1" />
            Update
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default withRouter(withTranslation()(AllowanceSettings));